import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { fmt } from "@/pages/shop/Shop";
import CheckoutDialog from "@/components/CheckoutDialog";
import PageHero from "@/components/PageHero";

export default function Cart() {
  const { lang, pickL } = useI18n();
  const { user } = useAuth();
  const { items, setQty, remove, clear, subtotal, total } = useCart();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const de = lang === "de";
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (params.get("cancelled")) toast.info(de ? "Zahlung abgebrochen. Ihr Warenkorb ist gespeichert – bitte wählen Sie eine andere Zahlungsart." : "Payment cancelled. Your cart is saved — please choose another method.");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkout = async () => {
    if (!user) { nav(`/login?next=${encodeURIComponent("/shop/cart")}`); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/shop/checkout", { items: items.map((i) => ({ product_id: i.product_id, qty: i.qty })) });
      localStorage.setItem("jd_pending_shop", data.shop_order_id);
      setOrder({ id: data.shop_order_id, total: data.total, currency: data.currency });
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  if (items.length === 0 && !order) return (
    <div>
      <PageHero eyebrow="Shop" title={de ? "Warenkorb" : "Cart"} />
      <div className="container-x py-20 text-center" data-testid="cart-empty">
        <ShoppingBag size={56} className="mx-auto text-muted-foreground mb-6" />
        <p className="text-muted-foreground">{de ? "Ihr Warenkorb ist leer." : "Your cart is empty."}</p>
        <Link to="/shop" className="btn-primary mt-6 inline-flex" data-testid="cart-shop-link">{de ? "Weiter einkaufen" : "Continue shopping"}</Link>
      </div>
    </div>
  );

  return (
    <div>
      <PageHero eyebrow="Shop" title={de ? "Warenkorb" : "Cart"} />
      <section className="container-x py-12 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
          {items.map((it) => (
            <div key={it.product_id} className="surface p-4 flex items-center gap-4" data-testid={`cart-item-${it.product_id}`}>
              <Link to={`/shop/${it.slug}`} className="h-20 w-20 rounded-md overflow-hidden bg-secondary shrink-0">
                {it.image ? <img src={it.image} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full flex items-center justify-center text-muted-foreground"><ShoppingBag size={22} /></div>}
              </Link>
              <div className="flex-1 min-w-0">
                <Link to={`/shop/${it.slug}`} className="font-semibold hover:text-primary transition-colors">{pickL(it, "name")}</Link>
                <div className="text-sm text-muted-foreground">{fmt(it.sale_price || it.price)}</div>
              </div>
              <div className="flex items-center gap-1 border border-border rounded-lg">
                <button onClick={() => setQty(it.product_id, it.qty - 1)} className="p-2 hover:text-primary" data-testid={`cart-dec-${it.product_id}`}><Minus size={14} /></button>
                <span className="w-8 text-center text-sm font-semibold" data-testid={`cart-qty-${it.product_id}`}>{it.qty}</span>
                <button onClick={() => setQty(it.product_id, it.qty + 1)} className="p-2 hover:text-primary" data-testid={`cart-inc-${it.product_id}`}><Plus size={14} /></button>
              </div>
              <div className="w-24 text-right font-display font-bold">{fmt((it.sale_price || it.price) * it.qty)}</div>
              <button onClick={() => remove(it.product_id)} className="p-2 text-muted-foreground hover:text-destructive" data-testid={`cart-remove-${it.product_id}`}><Trash2 size={16} /></button>
            </div>
          ))}
          <button onClick={clear} className="text-sm link-muted" data-testid="cart-clear">{de ? "Warenkorb leeren" : "Clear cart"}</button>
        </div>

        <div className="lg:col-span-1">
          <div className="surface p-6 sticky top-24">
            <h2 className="font-display font-bold text-lg mb-5">{de ? "Zusammenfassung" : "Summary"}</h2>
            <div className="space-y-2.5 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">{de ? "Zwischensumme" : "Subtotal"}</span><span data-testid="cart-subtotal">{fmt(subtotal)}</span></div>
              <p className="text-xs text-muted-foreground">{de ? "Gemäß § 19 UStG wird keine Umsatzsteuer berechnet." : "No VAT is charged pursuant to § 19 UStG."}</p>
              <div className="border-t border-border pt-3 flex justify-between font-display font-black text-lg"><span>{de ? "Gesamt" : "Total"}</span><span data-testid="cart-total">{fmt(total)}</span></div>
            </div>
            <button onClick={checkout} disabled={loading} className="btn-primary w-full mt-6 !py-3" data-testid="cart-checkout">
              {loading ? <Loader2 size={18} className="animate-spin" /> : <>{de ? "Zur Kasse" : "Checkout"} <ArrowRight size={16} /></>}
            </button>
            <Link to="/shop" className="block text-center text-sm link-muted mt-4">{de ? "Weiter einkaufen" : "Continue shopping"}</Link>
          </div>
        </div>
      </section>

      {order && (
        <CheckoutDialog
          checkoutBody={{ shop_order_id: order.id }}
          summary={{ label: de ? "Bestellung" : "Order", total: order.total, currency: order.currency }}
          onClose={() => setOrder(null)}
          onPaid={() => { clear(); localStorage.removeItem("jd_pending_shop"); nav("/portal/purchases"); }}
        />
      )}
    </div>
  );
}
